import React, { useState, useEffect } from 'react';
import { X, Loader2, CheckCircle, Calendar, HelpCircle } from 'lucide-react';
import PrivacyConsent from '../forms/PrivacyConsent';
import { facebookPixelEvents } from '../../utils/facebookPixel';
import sendGridService from '../../lib/api/sendGridService';

interface DatePreferenceModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  homeschoolType: string;
  studentAge: string;
  homeschoolRole: string;
  preferredDays: string[];
  preferredTimes: string[];
  additionalComments: string;
}

interface UTMParams {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
}

const sendGridListId = import.meta.env.VITE_SENDGRID_LIST_DATE_PREFERENCE_ID;



export default function DatePreferenceModal({ 
  isOpen, 
  onClose 
}: DatePreferenceModalProps) {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    homeschoolType: '',
    studentAge: '',
    homeschoolRole: '',
    preferredDays: [],
    preferredTimes: [],
    additionalComments: ''
  });
  const [utmParams, setUtmParams] = useState<UTMParams>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);

  // Capture UTM parameters on component mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const utms: UTMParams = {};
    
    ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'].forEach(param => {
      const value = urlParams.get(param);
      if (value) {
        utms[param as keyof UTMParams] = value;
      }
    });
    
    setUtmParams(utms);
    
    if (Object.keys(utms).length > 0) {
      sessionStorage.setItem('utmParams', JSON.stringify(utms));
    } else {
      const storedUtms = sessionStorage.getItem('utmParams');
      if (storedUtms) {
        setUtmParams(JSON.parse(storedUtms));
      }
    }
  }, []);

  // Track modal open
  useEffect(() => {
    if (isOpen) {
      // Track form view
      if (typeof window.gtag !== 'undefined') {
        window.gtag('event', 'form_view', {
          form_type: 'date_preference_interest',
          event_category: 'engagement'
        });
      }

      // Facebook Pixel form view tracking
      facebookPixelEvents.viewContent({
        content_name: 'Date Preference Interest Form',
        content_category: 'form_view',
        content_type: 'date_preference_interest'
      });
    }
  }, [isOpen]);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setStep(1);
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        homeschoolType: '',
        studentAge: '',
        homeschoolRole: '',
        preferredDays: [],
        preferredTimes: [],
        additionalComments: ''
      });
      setError('');
      setShowSuccess(false);
    }
  }, [isOpen]);

  const handleNext = (e: React.FormEvent) => {
    e.preventDefault();
    if (step === 1 && formData.homeschoolType && formData.studentAge && formData.homeschoolRole) {
      setStep(2);
    } else if (step === 2 && formData.firstName && formData.lastName && formData.email) {
      setStep(3);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (step < 3) {
      handleNext(e);
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      if (!sendGridListId) {
        throw new Error('SendGrid List ID is not configured. Please set VITE_SENDGRID_LIST_HS_SUMMER25_ID environment variable.');
      }

      // Track form submission with Facebook Pixel
      facebookPixelEvents.lead({
        content_name: 'Date Preference Interest Submission',
        content_category: 'date_preference_interest',
        content_type: 'form_submission'
      });

      // Submit to SendGrid with date preference specific data
      const sendGridSuccess = await sendGridService.addDatePreferenceContact(
        formData,
        {
          ...utmParams,
          utm_content: 'date_preference_interest',
          utm_campaign: `${utmParams.utm_campaign || 'organic'}_date_preference`
        },
        sendGridListId
      );

      if (sendGridSuccess) {
        // Track successful interest submission
        facebookPixelEvents.completeRegistration({
          content_name: 'Date Preference Interest Complete',
          content_category: 'date_preference_interest',
          content_type: 'registration_success'
        });
        
        setShowSuccess(true);
        setIsSubmitting(false);
        
        // Auto close after success
        setTimeout(() => {
          onClose();
        }, 8000);
      } else {
        throw new Error('Interest submission failed');
      }
    } catch (err: any) {
      console.error('Date preference submission error:', err);
      setError('Something went wrong. Please try again.');
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleCheckboxChange = (name: 'preferredDays' | 'preferredTimes', value: string) => {
    setFormData(prev => ({
      ...prev,
      [name]: prev[name].includes(value)
        ? prev[name].filter(item => item !== value)
        : [...prev[name], value]
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="relative bg-gradient-to-br from-blue-500 to-purple-600 px-8 py-6 text-white">
          <button 
            onClick={onClose}
            className="absolute top-4 right-4 p-1 hover:bg-white/10 rounded-full transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
          
          <div className="flex items-center space-x-4">
            <div className="bg-white/20 p-3 rounded-full">
              <Calendar className="w-8 h-8" />
            </div>
            <div>
              <h2 className="text-2xl font-bold mb-1">
                📅 Show Your Interest
              </h2>
              <p className="text-blue-50">
                Help us schedule classes that work better for you
              </p>
            </div>
          </div>
        </div>

        <div className="p-8">
          {showSuccess ? (
            <div className="text-center">
              <div className="mb-6 text-blue-600">
                <CheckCircle className="w-20 h-20 mx-auto" />
              </div>
              <h3 className="text-2xl font-bold mb-4 text-gray-900">
                🎊 Thank You for Your Interest!
              </h3>
              <p className="text-gray-600 mb-4 text-lg">
                We've received your scheduling preferences and will use this information to plan future classes.
              </p>
              <div className="bg-blue-50 rounded-xl p-6 mb-6">
                <h4 className="font-semibold text-blue-900 mb-3">What happens next?</h4>
                <ul className="text-left space-y-2 text-blue-800">
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" />
                    You'll receive a confirmation email with your preferences
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" />
                    We'll notify you when classes matching your preferences are scheduled
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" />
                    Priority access to classes that fit your preferred times
                  </li>
                </ul>
              </div>
              <p className="text-gray-500 text-sm">
                This window will close automatically in a few seconds...
              </p>
            </div>
          ) : (
            <>
              {/* Progress Indicator */}
              <div className="mb-8">
                <div className="flex items-center justify-center space-x-4">
                  {[1, 2, 3].map((stepNum) => (
                    <div key={stepNum} className="flex items-center">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center font-semibold ${
                        stepNum === step 
                          ? 'bg-blue-500 text-white' 
                          : stepNum < step 
                            ? 'bg-green-500 text-white'
                            : 'bg-gray-200 text-gray-500'
                      }`}>
                        {stepNum < step ? '✓' : stepNum}
                      </div>
                      {stepNum < 3 && (
                        <div className={`w-12 h-1 mx-2 ${
                          stepNum < step ? 'bg-green-500' : 'bg-gray-200'
                        }`} />
                      )}
                    </div>
                  ))}
                </div>
                <div className="text-center mt-2 text-sm text-gray-600">
                  Step {step} of 3
                </div>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-6">
                {step === 1 ? (
                  <>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                      Step 1: Tell us about your homeschool setup
                    </h3>
                    
                    <div>
                      <label htmlFor="homeschoolRole" className="block text-sm font-medium text-gray-700 mb-2">
                        Your Role <span className="text-red-500">*</span>
                      </label>
                      <select
                        id="homeschoolRole"
                        name="homeschoolRole"
                        value={formData.homeschoolRole}
                        onChange={handleChange}
                        required
                        className="block w-full border border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 p-3"
                      >
                        <option value="">Select your role</option>
                        <option value="homeschool_parent">Homeschool Parent</option>
                        <option value="homeschool_teacher">Homeschool Educator</option>
                        <option value="other">Other</option>
                      </select>
                    </div>

                    <div>
                      <label htmlFor="homeschoolType" className="block text-sm font-medium text-gray-700 mb-2">
                        Type of Homeschool <span className="text-red-500">*</span>
                      </label>
                      <select
                        id="homeschoolType"
                        name="homeschoolType"
                        value={formData.homeschoolType}
                        onChange={handleChange}
                        required
                        className="block w-full border border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 p-3"
                      >
                        <option value="">Select type</option>
                        <option value="individual_household">Individual Household</option>
                        <option value="homeschool_coop">Homeschool Cooperative</option>
                        <option value="micro_schools">Micro School</option>
                        <option value="other">Other</option>
                      </select>
                    </div>

                    <div>
                      <label htmlFor="studentAge" className="block text-sm font-medium text-gray-700 mb-2">
                        Student Age <span className="text-red-500">*</span>
                      </label>
                      <select
                        id="studentAge"
                        name="studentAge"
                        value={formData.studentAge}
                        onChange={handleChange}
                        required
                        className="block w-full border border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 p-3"
                      >
                        <option value="">Select age range</option>
                        <option value="middle_school">Middle School</option>
                        <option value="high_school">High School</option>
                        <option value="m_h_school">Middle & High School</option>
                        <option value="other">Other</option>
                      </select>
                    </div>

                    <button
                      type="submit"
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors"
                    >
                      Next Step →
                    </button>
                  </>
                ) : step === 2 ? (
                  <>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                      Step 2: Your contact information
                    </h3>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-2">
                          First Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="firstName"
                          id="firstName"
                          value={formData.firstName}
                          onChange={handleChange}
                          required
                          className="block w-full border border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 p-3"
                          placeholder="First name"
                        />
                      </div>
                      <div>
                        <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-2">
                          Last Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="lastName"
                          id="lastName"
                          value={formData.lastName}
                          onChange={handleChange}
                          required
                          className="block w-full border border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 p-3"
                          placeholder="Last name"
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                        Email Address <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        name="email"
                        id="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="block w-full border border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 p-3"
                        placeholder="your@email.com"
                      />
                    </div>

                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                        Phone Number <span className="text-gray-500 text-sm">(optional)</span>
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        id="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="block w-full border border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 p-3"
                        placeholder="+1 (555) 123-4567"
                      />
                    </div>

                    <div className="flex gap-4">
                      <button
                        type="button"
                        onClick={() => setStep(1)}
                        className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold py-3 px-6 rounded-lg transition-colors"
                      >
                        ← Back
                      </button>
                      <button
                        type="submit"
                        className="flex-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors"
                      >
                        Next Step →
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                      Step 3: Your scheduling preferences
                    </h3>

                    <div className="space-y-6">
                      <div>
                        <div className="flex items-center mb-4">
                          <label className="block text-sm font-medium text-gray-700">
                            Which days work best for you? <span className="text-red-500">*</span>
                          </label>
                          <div className="relative group ml-2">
                            <HelpCircle className="h-4 w-4 text-gray-400" />
                            <div className="absolute left-full ml-2 top-1/2 -translate-y-1/2 hidden group-hover:block w-64 p-2 bg-gray-900 text-white text-sm rounded-md shadow-lg z-10">
                              Select all days that could work for your schedule
                            </div>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map((day) => (
                            <label key={day} className="flex items-center p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                              <input
                                type="checkbox"
                                checked={formData.preferredDays.includes(day)}
                                onChange={() => handleCheckboxChange('preferredDays', day)}
                                className="mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                              />
                              <span className="text-sm font-medium text-gray-700">{day}</span>
                            </label>
                          ))}
                        </div>
                      </div>

                      <div>
                        <div className="flex items-center mb-4">
                          <label className="block text-sm font-medium text-gray-700">
                            What times work best for you? <span className="text-red-500">*</span>
                          </label>
                          <div className="relative group ml-2">
                            <HelpCircle className="h-4 w-4 text-gray-400" />
                            <div className="absolute left-full ml-2 top-1/2 -translate-y-1/2 hidden group-hover:block w-64 p-2 bg-gray-900 text-white text-sm rounded-md shadow-lg z-10">
                              Select all time slots that could work for your family
                            </div>
                          </div>
                        </div>
                        <div className="grid grid-cols-1 gap-3">
                          {[
                            'Morning (9:00 AM - 12:00 PM EST)',
                            'Early Afternoon (12:00 PM - 3:00 PM EST)',
                            'Late Afternoon (3:00 PM - 6:00 PM EST)',
                            'Evening (6:00 PM - 9:00 PM EST)',
                            'Weekend Morning (9:00 AM - 12:00 PM EST)',
                            'Weekend Afternoon (12:00 PM - 6:00 PM EST)'
                          ].map((time) => (
                            <label key={time} className="flex items-center p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                              <input
                                type="checkbox"
                                checked={formData.preferredTimes.includes(time)}
                                onChange={() => handleCheckboxChange('preferredTimes', time)}
                                className="mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                              />
                              <span className="text-sm font-medium text-gray-700">{time}</span>
                            </label>
                          ))}
                        </div>
                      </div>

                      <div>
                        <label htmlFor="additionalComments" className="block text-sm font-medium text-gray-700 mb-2">
                          Additional Comments <span className="text-gray-500 text-sm">(optional)</span>
                        </label>
                        <textarea
                          name="additionalComments"
                          id="additionalComments"
                          value={formData.additionalComments}
                          onChange={handleChange}
                          rows={4}
                          className="block w-full border border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 p-3"
                          placeholder="Any specific scheduling needs or preferences we should know about?"
                        />
                      </div>
                    </div>

                    {error && (
                      <div className="text-red-600 text-sm bg-red-50 p-3 rounded-lg border border-red-200">
                        {error}
                      </div>
                    )}

                    <div>
                      <PrivacyConsent 
                        onConsentChange={(field, value) => console.log(field, value)}
                        required={true}
                      />
                    </div>

                    <div className="flex gap-4">
                      <button
                        type="button"
                        onClick={() => setStep(2)}
                        className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold py-3 px-6 rounded-lg transition-colors"
                      >
                        ← Back
                      </button>
                      <button
                        type="submit"
                        disabled={isSubmitting || formData.preferredDays.length === 0 || formData.preferredTimes.length === 0}
                        className="flex-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors disabled:opacity-70 disabled:cursor-not-allowed"
                      >
                        {isSubmitting ? (
                          <span className="flex items-center justify-center">
                            <Loader2 className="w-5 h-5 animate-spin mr-2" />
                            Submitting...
                          </span>
                        ) : (
                          '📅 Submit My Preferences'
                        )}
                      </button>
                    </div>

                    <p className="text-xs text-gray-500 text-center">
                      By submitting, you agree to our{' '}
                      <a href="/privacy-policy" className="text-blue-600 hover:underline">
                        Privacy Policy
                      </a>. We'll only contact you about class scheduling.
                    </p>
                  </>
                )}
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
} 